import { useState } from 'react';
import { useReviews, useReviewStats, useReview } from '@/hooks/useReviews';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  Bot,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  GitCommit,
  FileCode,
  TrendingUp,
  AlertCircle,
  ChevronRight,
  Filter,
  X,
  ExternalLink,
  ThumbsUp,
  Lightbulb,
  FileText,
} from 'lucide-react';
import { formatRelativeTime } from '@/utils/helpers';
import { CodeReview, IssueSeverity, FileReview, ReviewIssue } from '@/api/reviews.api';

/**
 * Short display label for a review: commit SHA, or "MR !<iid> @ <head sha>"
 * for merge request reviews (stored as `mr-<iid>[-<headSha>]`).
 */
function getReviewLabel(review: Pick<CodeReview, 'commitSha' | 'gitlabMrIid'>): string {
  const mrMatch = /^mr-(\d+)(?:-([0-9a-f]+))?$/i.exec(review.commitSha || '');
  const mrIid = review.gitlabMrIid ?? (mrMatch ? Number(mrMatch[1]) : undefined);
  if (mrIid !== undefined) {
    const headSha = mrMatch?.[2];
    return headSha ? `MR !${mrIid} @ ${headSha.substring(0, 8)}` : `MR !${mrIid}`;
  }
  return (review.commitSha || '').substring(0, 8);
}

/**
 * Severity badge component
 */
function SeverityBadge({ severity, count }: { severity: IssueSeverity; count: number }) {
  const config = {
    critical: { bg: 'bg-red-100', text: 'text-red-700', label: 'Critical' },
    major: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Major' },
    minor: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Minor' },
    suggestion: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Suggestion' },
  };

  const c = config[severity];

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${c.bg} ${c.text}`}>
      {count} {c.label}
    </span>
  );
}

/**
 * Score badge component
 */
function ScoreBadge({ score }: { score: number }) {
  let colorClass = 'bg-red-100 text-red-700';
  if (score >= 80) {
    colorClass = 'bg-green-100 text-green-700';
  } else if (score >= 60) {
    colorClass = 'bg-yellow-100 text-yellow-700';
  } else if (score >= 40) {
    colorClass = 'bg-orange-100 text-orange-700';
  }

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold ${colorClass}`}>
      {score}/100
    </span>
  );
}

/**
 * Status badge component
 */
function StatusBadge({ status }: { status: CodeReview['status'] }) {
  const config = {
    pending: { icon: Clock, bg: 'bg-blue-100', text: 'text-blue-700' },
    completed: { icon: CheckCircle, bg: 'bg-green-100', text: 'text-green-700' },
    failed: { icon: XCircle, bg: 'bg-red-100', text: 'text-red-700' },
    skipped: { icon: AlertCircle, bg: 'bg-gray-100', text: 'text-gray-700' },
  };

  const c = config[status];
  const Icon = c.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${c.bg} ${c.text}`}>
      <Icon className="h-3 w-3" />
      {status}
    </span>
  );
}

/**
 * Review card component
 */
function ReviewCard({ review, onClick, isSelected }: { review: CodeReview; onClick: () => void; isSelected: boolean }) {
  const criticalCount = review.issues?.filter(i => i.severity === 'critical').length || 0;
  const majorCount = review.issues?.filter(i => i.severity === 'major').length || 0;
  const minorCount = review.issues?.filter(i => i.severity === 'minor').length || 0;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  };

  return (
    <div onClick={handleClick} className="cursor-pointer">
      <Card
        className={`hover:border-primary/50 transition-colors ${isSelected ? 'border-primary ring-2 ring-primary bg-primary/5' : ''}`}
      >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-center gap-2 mb-2">
              <GitCommit className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <code className="text-sm font-mono bg-muted px-1.5 py-0.5 rounded">
                {getReviewLabel(review)}
              </code>
              <StatusBadge status={review.status} />
              {review.status === 'completed' && <ScoreBadge score={review.overallScore} />}
            </div>

            {/* Summary */}
            {review.summary && (
              <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                {review.summary}
              </p>
            )}

            {/* Issues */}
            {review.status === 'completed' && review.issues.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {criticalCount > 0 && <SeverityBadge severity="critical" count={criticalCount} />}
                {majorCount > 0 && <SeverityBadge severity="major" count={majorCount} />}
                {minorCount > 0 && <SeverityBadge severity="minor" count={minorCount} />}
              </div>
            )}

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              {review.repoId && typeof review.repoId === 'object' && (
                <span className="flex items-center gap-1">
                  <FileCode className="h-3 w-3" />
                  {review.repoId.name}
                </span>
              )}
              {review.userId && typeof review.userId === 'object' && (
                <span>by {review.userId.displayName || review.userId.username}</span>
              )}
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatRelativeTime(review.createdAt)}
              </span>
              {review.processingTimeMs > 0 && (
                <span>{(review.processingTimeMs / 1000).toFixed(1)}s</span>
              )}
              {review.developerNotified && (
                <span className="text-green-600">Developer notified</span>
              )}
              {review.gitlabCommentPosted && (
                <span className="text-blue-600">GitLab comment posted</span>
              )}
            </div>
          </div>

          <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
        </div>
      </CardContent>
    </Card>
    </div>
  );
}

/**
 * Review detail panel component
 */
function ReviewDetailPanel({
  reviewId,
  onClose
}: {
  reviewId: string;
  onClose: () => void;
}) {
  const { data: review, isLoading } = useReview(reviewId);

  if (isLoading) {
    return (
      <div className="fixed inset-y-0 right-0 w-full max-w-xl bg-background border-l shadow-lg z-50 overflow-y-auto">
        <div className="flex justify-center items-center h-full">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (!review) {
    return null;
  }

  // Build GitLab URL
  const getGitLabUrl = () => {
    if (review.commitId?.webUrl) {
      return review.commitId.webUrl;
    }
    if (review.repoId && typeof review.repoId === 'object' && review.repoId.gitlabUrl) {
      if (review.gitlabMrIid) {
        return `${review.repoId.gitlabUrl}/-/merge_requests/${review.gitlabMrIid}`;
      }
      return `${review.repoId.gitlabUrl}/-/commit/${review.commitSha}`;
    }
    return null;
  };

  const gitlabUrl = getGitLabUrl();

  const criticalIssues = review.issues.filter(i => i.severity === 'critical');
  const majorIssues = review.issues.filter(i => i.severity === 'major');
  const minorIssues = review.issues.filter(i => i.severity === 'minor');
  const suggestions = review.issues.filter(i => i.severity === 'suggestion');

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-xl bg-background border-l shadow-lg z-50 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-background border-b p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <GitCommit className="h-5 w-5 text-muted-foreground" />
          <div>
            <code className="text-sm font-mono font-bold">
              {getReviewLabel(review)}
            </code>
            <div className="text-xs text-muted-foreground">
              {review.repoId && typeof review.repoId === 'object' && review.repoId.name}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {gitlabUrl && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(gitlabUrl, '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              View in GitLab
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Score & Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="text-4xl font-bold">{review.overallScore}</div>
            <div>
              <div className="text-sm text-muted-foreground">Overall Score</div>
              <StatusBadge status={review.status} />
            </div>
          </div>
          <div className="text-right text-sm text-muted-foreground">
            <div>Complexity: {review.estimatedComplexity}</div>
            <div>{(review.processingTimeMs / 1000).toFixed(1)}s processing</div>
            {review.costUsd > 0 && <div>${review.costUsd.toFixed(4)} cost</div>}
          </div>
        </div>

        {/* Summary */}
        <div>
          <h3 className="font-semibold mb-2">Summary</h3>
          <p className="text-sm text-muted-foreground">{review.summary}</p>
        </div>

        {/* Issues by Severity */}
        {review.issues.length > 0 && (
          <div>
            <h3 className="font-semibold mb-3">Issues ({review.issues.length})</h3>

            {criticalIssues.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2 text-red-600">
                  <XCircle className="h-4 w-4" />
                  <span className="font-medium">Critical ({criticalIssues.length})</span>
                </div>
                <div className="space-y-2 pl-6">
                  {criticalIssues.map((issue, idx) => (
                    <IssueItem key={idx} issue={issue} />
                  ))}
                </div>
              </div>
            )}

            {majorIssues.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2 text-orange-600">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="font-medium">Major ({majorIssues.length})</span>
                </div>
                <div className="space-y-2 pl-6">
                  {majorIssues.map((issue, idx) => (
                    <IssueItem key={idx} issue={issue} />
                  ))}
                </div>
              </div>
            )}

            {minorIssues.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2 text-yellow-600">
                  <AlertCircle className="h-4 w-4" />
                  <span className="font-medium">Minor ({minorIssues.length})</span>
                </div>
                <div className="space-y-2 pl-6">
                  {minorIssues.map((issue, idx) => (
                    <IssueItem key={idx} issue={issue} />
                  ))}
                </div>
              </div>
            )}

            {suggestions.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2 text-blue-600">
                  <Lightbulb className="h-4 w-4" />
                  <span className="font-medium">Suggestions ({suggestions.length})</span>
                </div>
                <div className="space-y-2 pl-6">
                  {suggestions.map((issue, idx) => (
                    <IssueItem key={idx} issue={issue} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Positives */}
        {review.positives && review.positives.length > 0 && (
          <div>
            <h3 className="font-semibold mb-2 flex items-center gap-2 text-green-600">
              <ThumbsUp className="h-4 w-4" />
              Positives
            </h3>
            <ul className="space-y-1 text-sm text-muted-foreground">
              {review.positives.map((positive, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  {positive}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Recommendations */}
        {review.recommendations && review.recommendations.length > 0 && (
          <div>
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Lightbulb className="h-4 w-4" />
              Recommendations
            </h3>
            <ul className="space-y-1 text-sm text-muted-foreground">
              {review.recommendations.map((rec, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* File Reviews */}
        {review.fileReviews && review.fileReviews.length > 0 && (
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              File Reviews ({review.fileReviews.length})
            </h3>
            <div className="space-y-3">
              {review.fileReviews.map((fileReview, idx) => (
                <FileReviewItem key={idx} fileReview={fileReview} />
              ))}
            </div>
          </div>
        )}

        {/* Meta */}
        <div className="border-t pt-4 text-xs text-muted-foreground space-y-1">
          {review.userId && typeof review.userId === 'object' && (
            <div>Author: {review.userId.displayName || review.userId.username}</div>
          )}
          <div>Created: {new Date(review.createdAt).toLocaleString()}</div>
          {review.aiProvider && <div>AI Provider: {review.aiProvider} / {review.aiModel}</div>}
          {review.tokensUsed > 0 && <div>Tokens used: {review.tokensUsed.toLocaleString()}</div>}
          {review.developerNotified && review.developerNotifiedAt && (
            <div className="text-green-600">
              Developer notified at {new Date(review.developerNotifiedAt).toLocaleString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Issue item component
 */
function IssueItem({ issue }: { issue: ReviewIssue }) {
  return (
    <div className="text-sm border-l-2 border-muted pl-3 py-1">
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
        <span className="bg-muted px-1.5 py-0.5 rounded">{issue.category}</span>
        {issue.file && (
          <code className="text-xs">
            {issue.file}{issue.line ? `:${issue.line}` : ''}
          </code>
        )}
      </div>
      <div>{issue.message}</div>
      {issue.suggestion && (
        <div className="mt-1 text-xs text-muted-foreground">
          <span className="text-primary">💡</span> {issue.suggestion}
        </div>
      )}
    </div>
  );
}

/**
 * File review item component
 */
function FileReviewItem({ fileReview }: { fileReview: FileReview }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <code className="text-xs bg-muted px-2 py-0.5 rounded">{fileReview.path}</code>
          <ScoreBadge score={fileReview.score} />
        </div>
        {fileReview.summary && (
          <p className="text-xs text-muted-foreground">{fileReview.summary}</p>
        )}
        {fileReview.issues.length > 0 && (
          <div className="mt-2 text-xs text-muted-foreground">
            {fileReview.issues.length} issue(s) in this file
          </div>
        )}
      </CardContent>
    </Card>
  );
}

type FilterStatus = 'all' | 'completed' | 'pending' | 'failed';

export default function Reviews() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);

  // Fetch reviews
  const { data: reviewsData, isLoading } = useReviews({
    page,
    limit: 20,
    status: statusFilter === 'all' ? undefined : statusFilter,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const totalPages = reviewsData?.pagination?.totalPages ?? 0;

  // Fetch stats
  const { data: stats } = useReviewStats({ days: 30 });

  const statusFilters: { value: FilterStatus; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'completed', label: 'Completed' },
    { value: 'pending', label: 'Pending' },
    { value: 'failed', label: 'Failed' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">AI Code Reviews</h1>
          <p className="text-muted-foreground">
            View AI-powered code review results for commits and merge requests
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Reviews</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalReviews || 0}</div>
            <p className="text-xs text-muted-foreground">Last 30 days</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-600" />
              Avg Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.averageScore || 0}/100</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <XCircle className="h-4 w-4 text-red-600" />
              Critical Issues
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats?.criticalIssues || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              Major Issues
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats?.majorIssues || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Issues</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalIssues || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <div className="flex gap-2">
          {statusFilters.map((filter) => (
            <Button
              key={filter.value}
              variant={statusFilter === filter.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setStatusFilter(filter.value);
                setPage(1);
              }}
            >
              {filter.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Review List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : !reviewsData?.data?.length ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Bot className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No reviews found</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              AI code reviews will appear here when commits are pushed to repositories with webhooks enabled.
            </p>
            <div className="mt-6 text-sm text-muted-foreground">
              <p>Reviews are automatically triggered when:</p>
              <ul className="mt-2 space-y-1">
                <li>- A push event is received from GitLab</li>
                <li>- A merge request is opened or updated</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {reviewsData.data.map((review) => {
            const reviewId = review.id || (review as unknown as { _id: string })._id;
            return (
              <ReviewCard
                key={reviewId}
                review={review}
                onClick={() => setSelectedReviewId(reviewId)}
                isSelected={selectedReviewId === reviewId}
              />
            );
          })}
        </div>
      )}

      {/* Detail Panel with Backdrop */}
      {selectedReviewId && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setSelectedReviewId(null)}
          />
          <ReviewDetailPanel
            reviewId={selectedReviewId}
            onClose={() => setSelectedReviewId(null)}
          />
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
